/**
 * Renderer, scene and per-frame render of the CRM scan viewer, without React,
 * so a local test page can drive exactly what ScanModelViewer draws.
 */
import * as THREE from "three";
import { HoleBlurPass } from "./holeBlurPass";

/** Switch: false restores today's direct render (holes show the background). */
export const SCAN_VIEWER_HOLE_BLUR = true;
export const SCAN_VIEWER_BACKGROUND = 0x111318;

/**
 * Lights reach only untextured meshes: photo meshes are unlit
 * (scanViewerMaterials.js).
 */
function addLights(scene) {
  scene.add(new THREE.AmbientLight(0xffffff, 0.65));
  const key = new THREE.DirectionalLight(0xffffff, 1.1);
  key.position.set(4, 8, 6);
  scene.add(key, new THREE.DirectionalLight(0xffffff, 0.45));
}

/**
 * The canvas renderer. With the hole blur the scene is drawn into the pass's
 * own 4× MSAA target with depth, so the canvas needs neither; without it this
 * is today's renderer (antialias and depth on).
 */
function createRenderer(holeBlur, pixelRatio) {
  const renderer = new THREE.WebGLRenderer({ antialias: !holeBlur, alpha: false, depth: !holeBlur });
  renderer.setPixelRatio(pixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  return renderer;
}

/**
 * A HoleBlurPass whose shaders compile and link on this browser, or null. The
 * probe draws an empty scene through every pass once at 1 × 1 px, then frees
 * those targets (setSize allocates the real ones).
 */
function workingHoleBlurPass(renderer) {
  const pass = new HoleBlurPass(renderer, { background: SCAN_VIEWER_BACKGROUND });
  const probeCamera = new THREE.PerspectiveCamera();
  pass.setSize(1, 1);
  pass.render(new THREE.Scene(), probeCamera);
  if (!pass.failed()) {
    pass.disposeTargets();
    return pass;
  }
  pass.dispose();
  return null;
}

/**
 * `holeBlur` (default SCAN_VIEWER_HOLE_BLUR) renders through HoleBlurPass,
 * which does its own 4× MSAA; the canvas then has no antialiasing or depth
 * buffer of its own. If a pass shader fails to compile or link, the stage is
 * built the way the viewer was before (its own renderer, antialias and depth
 * on, no pass), before anything is drawn. setSize takes CSS pixels, like
 * renderer.setSize.
 */
export function createScanViewerStage({ holeBlur = SCAN_VIEWER_HOLE_BLUR, pixelRatio = 1 } = {}) {
  let renderer = createRenderer(holeBlur, pixelRatio);
  let pass = null;
  if (holeBlur) {
    pass = workingHoleBlurPass(renderer);
    if (!pass) {
      console.error("Scan viewer: hole blur unavailable on this browser; drawing without it.");
      renderer.dispose();
      renderer.forceContextLoss();
      renderer = createRenderer(false, pixelRatio);
    }
  }
  const scene = new THREE.Scene();
  addLights(scene);
  if (!pass) scene.background = new THREE.Color(SCAN_VIEWER_BACKGROUND);
  const drawingBuffer = new THREE.Vector2();

  return {
    renderer,
    scene,
    holeBlurPass: pass,
    setSize(width, height) {
      renderer.setSize(width, height);
      if (pass) {
        renderer.getDrawingBufferSize(drawingBuffer);
        pass.setSize(drawingBuffer.x, drawingBuffer.y);
      }
    },
    render(camera) {
      if (pass) pass.render(scene, camera);
      else renderer.render(scene, camera);
    },
    dispose() {
      pass?.dispose();
      renderer.dispose();
    },
  };
}
